import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationPopupComponent } from 'src/app/shared/component/confirmation-popup/confirmation-popup.component';
import { Context } from 'src/app/shared/models/context';
import { Employee, Message, PrivilegeType, TableData } from 'src/app/shared/models/domain';
import { AccountTransaction } from 'src/app/shared/models/finance';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { DataService } from 'src/app/shared/services/data.service';
import { FinanceService } from 'src/app/shared/services/finance.service';
import { ManageTransactionComponent } from '../manage-transaction/manage-transaction.component';
import { DomainService } from 'src/app/shared/services/domain.service';

@Component({
  selector: 'app-transactions',
  templateUrl: './transactions.component.html',
  styleUrls: ['./transactions.component.scss'],
})
export class TransactionsComponent implements OnInit {
  public transactions: AccountTransaction[] = [];
  public context: Context;
  public employees: Employee[] = [];
  public isLoading: boolean = true;
  public privilege: PrivilegeType;
  public tableData: TableData = { content: [], number: 1, size: 10, totalElements: 0 } as TableData;
  public pageSizes: number[] = [];
  private pageDataKey: string = 'm-l';
  public selectedFilter: string = '';
  public filters: string[] = [];
  public fromDate: Date;
  public toDate: Date;
  public filter: any;

  constructor(
    private modalService: NgbModal,
    private authService: AuthService,
    private commonService: CommonService,
    private financeService: FinanceService,
    private datePipe: DatePipe,
    private dataService: DataService,
    private domainService: DomainService,
  ) { }

  ngOnInit(): void {
    this.dataService.privilege().subscribe(privilege => {
      this.privilege = this.commonService.getPrivileges(privilege);
    });
    this.context = this.authService.getContextData();
    this.filters = this.domainService.getDateFiltersTypes();
    this.selectedFilter = this.filters[2] || 'Last Week';
    this.filter = {
      from: '',
      to: ''
    };
    this.pageSizes = this.commonService.getDefaultPageSizes();
    this.tableData = this.commonService.getDefaultPageData(this.pageDataKey);
    this.fromDate = this.commonService.adjustDateByDays(-30);
    this.toDate = new Date();
    if (this.context) {
      this.getTransactions();
    }
  }

  getTransactions(clear: boolean = true, pageNumber: number = 0) {
    if (clear) {
      this.isLoading = true;
      this.tableData.content = [];
    }

    const pageSize = Number(this.tableData.size);

    // Format fromDate and toDate if they're set
    const fromDate = this.fromDate ? this.datePipe.transform(this.fromDate, 'dd-MM-yyyy') : undefined;
    const toDate = this.toDate ? this.datePipe.transform(this.toDate, 'dd-MM-yyyy') : undefined;

    this.financeService.getAccountTransactionsByPge(pageNumber, pageSize, null, fromDate, toDate)
      .subscribe({
        next: (tableData: TableData) => {
          this.tableData = {
            ...tableData,
            number: pageNumber + 1,
          };
          this.tableData.size = pageSize;
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.commonService.setToastr(500, error?.error?.name || error.error);
        }
      });
  }


  public deleteTransaction(transaction: AccountTransaction, index: number) {
    if (!this.privilege.canDelete) return;
    const modalRef = this.modalService.open(ConfirmationPopupComponent);
    modalRef.componentInstance.title = 'Transaction deletion';
    modalRef.componentInstance.prefixMessage = 'Are you sure you want to delete';
    modalRef.componentInstance.message = 'Transaction';
    modalRef.componentInstance.label = 'transaction';
    modalRef.result.then((isConfirmed: boolean) => {
      if (isConfirmed) {
        transaction.isDeleteInProgress = true;
        this.financeService.deleteAccountTransaction(transaction.id).subscribe({
          next: (response: Message) => {
            if (response) {
              this.commonService.setToastr(200, 'The transaction has been successfully deleted!');
              this.tableData.content.splice(index, 1);
              this.getTransactions(false);
              transaction.isDeleteInProgress = false;
            } else {
              this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
              transaction.isDeleteInProgress = false;
            }
          },
          error: (error: HttpErrorResponse) => {
            this.commonService.setToastr(500, error?.error?.name || 'Failed to delete transaction.');
            transaction.isDeleteInProgress = false;
          }
        });
      }
    });
  }

  public upsertTransaction(transaction?: AccountTransaction) {
    if (!this.privilege.canEdit) return;
    const modalRef = this.modalService.open(ManageTransactionComponent, { size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.transaction = transaction;
    modalRef.componentInstance.context = this.context;
    modalRef.result.then((response: Message) => {
      if (!response.failed) {
        this.getTransactions(false);
      }
    });
  }

  public onTransactionChange() {
    this.getTransactions(false);
  }

  public loadTableData(e: any) {
    this.tableData.size = +this.tableData.size;
    this.tableData.number = 1;
    this.commonService.storePageData(this.pageDataKey, this.tableData.size, this.tableData.number);
    this.getTransactions(true);
  }

  public onPageChange(newPage: number): void {
    this.tableData.number = newPage;
    const pageNumber = newPage - 1;
    this.getTransactions(false, pageNumber);
  }

  public filterAndFetchTransation(filter: string, element?: any) {
    const now = new Date();
    const today = new Date(now.setHours(0, 0, 0, 0));
    let fromDate: Date;
    let toDate: Date;

    switch (filter) {
      case 'Today':
        fromDate = new Date(today);
        toDate = new Date(today);
        toDate.setHours(23, 59, 59, 999);
        break;
      case 'Yesterday':
        fromDate = new Date(today);
        fromDate.setDate(today.getDate() - 1);
        toDate = new Date(today);
        toDate.setDate(today.getDate() - 1);
        toDate.setHours(23, 59, 59, 999);
        break;
      case 'Last Week':
        fromDate = new Date(today);
        fromDate.setDate(today.getDate() - 7);
        toDate = new Date(today);
        toDate.setHours(23, 59, 59, 999);
        break;
      case 'Custom':
        if (!this.fromDate || !this.toDate) {
          this.commonService.setToastr(400, 'Please select the date range!');
          return;
        }
        fromDate = new Date(this.fromDate);
        toDate = new Date(this.toDate);
        toDate.setHours(23, 59, 59, 999);
        break;
      default:
        return;
    }

    element?.close();
    this.fromDate = fromDate;
    this.toDate = toDate;
    this.getTransactions();
  }
}